<template>
  <div v-if="model.schedule">
    <page-header :title="$t('game.edit')">
      <li class="breadcrumb-item">
        <router-link :to="{name:'schedules.index'}">{{ $tc('schedule.name', 2) }}</router-link>
      </li>
      <li class="breadcrumb-item">
        <router-link :to="{name:'schedules.show', params: { uuid: this.$route.params.uuid }}">{{ model.schedule.name }}
        </router-link>
      </li>
      <li class="breadcrumb-item">{{ $tc('game.name', 2) }}</li>
      <li class="breadcrumb-item">{{ $tc('round', 1) }} {{ model.round }}</li>
      <li class="breadcrumb-item">{{ model.homeTeam.name + ' : ' + model.awayTeam.name }}</li>
    </page-header>
    <page-content>
      <div class="mb-5">
          <ntm-block :title="$tc('action.name', 2)">
            <template slot="options">
              <button v-if="user.isSuperAdmin && ['SCHEDULED', 'NOT_PLAYED'].includes(model.status)" class="btn btn-sm btn-warning mr-2" @click="fakeStats()">
                FAKE STATS
              </button>
              <div class="mr-3 d-inline-block">
                <le-regenerate-game-code v-if="model.status === 'ARCHIVED' && !model.hasTeamForfeit" :game-uuid="model.uuid" @codeGenerated="fetchData"></le-regenerate-game-code>
                <le-generate-game-news v-if="!model.hasTeamForfeit" :game="model"></le-generate-game-news>
                <le-generate-game-image v-if="config.generateImage" :game="model"></le-generate-game-image>
                <le-copy-to-clipboard :text="liveGameWebsiteLink"></le-copy-to-clipboard>
                <le-qr-code v-if="liveGameWebsiteLink" :name="model.slug" :link="liveGameWebsiteLink"></le-qr-code>
                <le-y-t-live class="m-1" :schedule="model.schedule" :game="model" @changed="fetchData"></le-y-t-live>
              </div>
              <button v-if="!model.hasTeamForfeit && model.status !== 'ARCHIVED'" class="btn btn-sm btn-danger" @click="openTeamForfeitModal()">{{$t('game.teamForfeit')}}</button>
              <button type="button"
                      v-if="!isEditMode"
                      @click="editMode()"
                      :disabled="model.status === 'LIVE'"
                      class="btn btn-sm btn-success">{{ $t('game.editGameStats') }}
              </button>
              <button type="button"
                      v-if="isEditMode"
                      @click="archive()"
                      class="btn btn-sm btn-success">{{ $t('archive.action') }}
              </button>
            </template>
          </ntm-block>
          <ntm-block :collapsable="false" :title="$t('game.gameScheduling')">
            <template v-slot:options>
              <span class="badge" :class="gameStatusClass">{{ $tc(`game.status.${model.status}`, 1) }}</span>
            </template>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="mr-2">
                <le-schedule-game :game="model" :venues="model.venues" class="mr-2"></le-schedule-game>
                <span class="text-nowrap" v-if="model.code">{{
                    formattedGameCode(model.code)
                  }}</span>
              </div>
              <span class="mr-2">{{model.time ? $d(new Date(model.time), 'dateTimeLong') : ''}}</span>
              <span class="mr-2">{{model.venue?.name}}</span>
            </div>
          </ntm-block>
      </div>

      <ntm-block>
        <table class="table text-center">
          <thead>
          <tr>
            <th style="width: 50%;">{{ $t('home') }}</th>
            <th style="width: 50%;">{{ $t('away') }}</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td><h3 class="font-w400">{{ model.homeTeam.name }}</h3></td>
            <td><h3 class="font-w400">{{ model.awayTeam.name }}</h3></td>
          </tr>
          </tbody>
        </table>
        <h3 class="text-center"></h3>
      </ntm-block>
      <form @submit.prevent="onSubmit()">
        <ntm-block :title="$tc('information', 2)">
          <formly-form :form="form" :model="model" :fields="infoFields"></formly-form>
          <button class="btn btn-success push-15">{{ $t('save') }}</button>
        </ntm-block>
      </form>
      <div class="game-stats" v-if="isEditMode">
        <div class="row">
          <div class="col-sm-6">
            <ntm-block :title="model.homeTeam.name">
              <vue-dropzone :useCustomSlot="true" ref="myVueDropzone" class="mb-3" id="dropzone" @vdropzone-complete="fetchData()" :options="dropzoneHomeTeam">
                <div class="dropzone-custom-content">
                  <h3 class="dropzone-custom-title">{{ $tc('import.playerStats.window') }}</h3>
                  <div class="subtitle">{{ $tc('import.orSelectFromPC') }}</div>
                </div>
              </vue-dropzone>
            </ntm-block>
          </div>
          <div class="col-sm-6">
            <ntm-block :title="model.awayTeam.name">
              <vue-dropzone :useCustomSlot="true" ref="myVueDropzone" class="mb-3" id="dropzone" @vdropzone-complete="fetchData()" :options="dropzoneAwayTeam">
                <div class="dropzone-custom-content">
                  <h3 class="dropzone-custom-title">{{ $tc('import.playerStats.window') }}</h3>
                  <div class="subtitle">{{ $tc('import.orSelectFromPC') }}</div>
                </div>
              </vue-dropzone>
            </ntm-block>
          </div>
        </div>
        <ntm-block>
          <h1 class="text-center text-uppercase">{{ $tc('stats') }}</h1>
        </ntm-block>
        <form @submit.prevent="onStatSubmit()">
          <div class="row">
            <div class="col-sm-6">
              <ntm-block :title="model.homeTeam.name">
                <formly-form :form="gameStatsForm" :model="model" :fields="baseHomeTeamStatsFields"></formly-form>
              </ntm-block>
            </div>
            <div class="col-sm-6">
              <ntm-block :title="model.awayTeam.name">
                <formly-form :form="gameStatsForm" :model="model" :fields="baseAwayTeamStatsFields"></formly-form>
              </ntm-block>
            </div>
          </div>

          <ntm-block :title="$tc('period_score', 2)">
            <table class="table mb-0">
              <thead>
                <tr>
                  <th>{{ $t('period') }}</th>
                  <th>{{ $t('home') }}</th>
                  <th>{{ $t('away') }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(period, index) in model.periods" :key="index">
                  <td>
                    <span v-if="period.periodType === 'REGULAR'">{{ $t('period') }} {{ period.periodNumber }}</span>
                    <span v-else>OT-{{ period.periodNumber}}</span>
                  </td>
                  <td>
                    <input type="number" v-model="period.homeScore" class="form-control form-control-sm">
                  </td>
                  <td>
                    <input type="number" v-model="period.awayScore" class="form-control form-control-sm">
                  </td>
                  <td>
                    <button v-if="!period.uuid" type="button" class="btn btn-sm btn-danger" v-tooltip="$t('tooltip.removePeriod')" @click="removePeriod(index)"><i class="fa fa-times"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
            <button type="button" class="btn btn-sm btn-primary mb-3" v-tooltip="$t('tooltip.addPeriod')" @click="addPeriod()"><i class="fa fa-plus"></i></button>
          </ntm-block>

          <div class="row">
            <div class="col-sm-12">
              <ntm-block>
                <formly-form :form="gameStatsForm" :model="model" :fields="gameStatsFields"></formly-form>
                <button class="btn btn-success push-15">{{ $t('save') }}</button>
              </ntm-block>
            </div>
          </div>

        </form>
        <div class="row">
          <div class="col-md-6">
            <ntm-block :title="model.homeTeam.name">
              <template #options>
                <button class="btn btn-info btn-sm" @click="playerSwapOpen('homeTeam')">{{ $t('game.player.stats.swap') }}</button>
              </template>
            </ntm-block>
            <ntm-block :title="$tc('player.name', 2)">
              <ntm-list>
                <ntm-list-item v-for="player in model.homeTeam.players"
                              @playerClicked="playerStatsModal(model.homeTeam.uuid, player.uuid)" :key="player.uuid"
                               :title="player.name" :img="player.profile">
                  {{ player.number_of_jersey }}
                  <template #actions>
                    <button @click="deletePlayerStats(player)" v-if="player.hasPlayed" class="btn btn-sm btn-danger" v-tooltip="$t('tooltip.deletePlayerStats')"><i class="fa fa-trash"></i></button>
                  </template>
                </ntm-list-item>
              </ntm-list>
            </ntm-block>
            <ntm-block :title="$t('team.stats')">
              <div v-if="model.homeTeam.stats" class="table-responsive">
                <table class="table">
                  <tbody>
                  <tr v-for="(stat, n) in model.homeTeam.stats" :key="n">
                    <td>{{ $t('formly.fields.' + n) }}</td>
                    <td>{{ stat }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </ntm-block>
          </div>
          <div class="col-md-6">
            <ntm-block :title="model.awayTeam.name">
              <template #options>
                <button class="btn btn-info btn-sm" @click="playerSwapOpen('awayTeam')">{{ $t('game.player.stats.swap') }}</button>
              </template>
            </ntm-block>
            <ntm-block :title="$tc('player.name', 2)">
              <ntm-list>
                <ntm-list-item v-for="player in model.awayTeam.players"
                               @playerClicked="playerStatsModal(model.awayTeam.uuid, player.uuid)" :key="player.uuid"
                               :title="player.name" :img="player.profile">
                  {{ player.number_of_jersey }}
                  <template #actions>
                    <button @click="deletePlayerStats(player)" v-if="player.hasPlayed" class="btn btn-sm btn-danger" v-tooltip="$t('tooltip.deletePlayerStats')"><i class="fa fa-trash"></i></button>
                  </template>
                </ntm-list-item>
              </ntm-list>
            </ntm-block>
            <ntm-block :title="$t('team.stats')">
              <div v-if="model.awayTeam.stats" class="table-responsive">
                <table class="table">
                  <tbody>
                  <tr v-for="(stat, n) in model.awayTeam.stats" :key="n">
                    <td>{{ $t('formly.fields.' + n) }}</td>
                    <td>{{ stat }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </ntm-block>
          </div>
        </div>
<!--        <form @submit.prevent="onCloneSubmit()">-->
<!--          <ntm-block :title="$t('game.clone')">-->
<!--            <formly-form :form="form" :model="cloneModel" :fields="cloneFields"></formly-form>-->
<!--            <button class="btn btn-success push-15">{{ $t('game.clone') }}</button>-->
<!--          </ntm-block>-->
<!--        </form>-->
      </div>
      <ntm-block :title="$tc('action.name', 2)">
        <template slot="options">
          <button type="button"
                  class="btn btn-sm btn-info"
                  @click="openMoveGameModal()">{{ $t('game.moveToAnotherSchedule') }}
          </button>
          <button type="button"
                  @click="resetStats()"
                  class="btn btn-sm btn-warning">{{ $t('game.resetStats') }}
          </button>
          <button type="button"
                  @click="deleteGame(model.uuid)"
                  class="btn btn-sm btn-danger">{{ $t('game.delete') }}
          </button>
        </template>
      </ntm-block>
      <ntm-modal @closed="fetchData" size="lg" :title="$tc('game.player.stats.name')" ref="playerStats">
        <template slot="content">
          <ntm-block :collapsable="false">
            <formly-form :form="playerForm" :model="playerStat" :fields="playerStatFields"></formly-form>
          </ntm-block>
        </template>
        <template slot="footer">
          <button class="btn btn-success push-15" @click="updatePlayerStats()">{{ $t('save') }}</button>
        </template>
      </ntm-modal>
      <modal name="swap-player-stats" :adaptive="true" height="auto" @closed="resetPlayerSwapModel()">
        <form @submit.prevent="playerSwapSubmit()">
          <ntm-block :collapsable="false" :title="$t('game.player.stats.swap')">
            <template #options>
              <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('swap-player-stats')" v-tooltip="$t('close')">
                <i class="fa fa-times"></i>
              </button>
            </template>
            <formly-form :form="playerSwapForm" :model="playerSwapModel" :fields="playerSwapFields"></formly-form>
            <button class="btn btn-success mb-3" :disabled="playerSwapLoading"><span v-if="!playerSwapLoading">{{ $t('save') }}</span><i v-if="playerSwapLoading" class="fa fa-cog fa-spin"></i></button>
          </ntm-block>
        </form>
      </modal>
      <modal name="team-forfeit" :adaptive="true" height="auto" @closed="resetPlayerSwapModel()">
        <form @submit.prevent="submitTeamForfeit()">
          <ntm-block :collapsable="false" :title="$t('game.teamForfeit')">
            <template #options>
              <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('team-forfeit')">
                <i class="fa fa-times"></i>
              </button>
            </template>
            <formly-form :form="teamForfeitForm" :model="teamForfeitModel" :fields="teamForfeitFields"></formly-form>
            <button class="btn btn-success mb-3" :disabled="playerSwapLoading"><span v-if="!playerSwapLoading">{{ $t('save') }}</span><i v-if="playerSwapLoading" class="fa fa-cog fa-spin"></i></button>
          </ntm-block>
        </form>
      </modal>
      <!-- Move game to another schedule modal -->
      <modal name="move-game" :adaptive="true" height="auto" @closed="resetMoveGameForm()">
        <form @submit.prevent="submitMoveGame()">
          <ntm-block :collapsable="false" :title="$t('game.moveToAnotherSchedule')">
            <template #options>
              <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('move-game')">
                <i class="fa fa-times"></i>
              </button>
            </template>
            <formly-form :form="moveGameForm" :model="moveGameModel" :fields="moveGameFields"></formly-form>
            <button class="btn btn-success mb-3" :disabled="moveGameLoading">
              <span v-if="!moveGameLoading">{{ $t('save') }}</span>
              <i v-else class="fa fa-cog fa-spin"></i>
            </button>
            <div v-if="moveGameErrorMessage" class="alert alert-danger mb-0">
              {{ $t(moveGameErrorMessage) }}
            </div>
          </ntm-block>
        </form>
      </modal>
    </page-content>
  </div>
</template>

<script>
import gameStatsFields from './formDefinitions/gameStatsFormDefinition'
import cloneFields from './formDefinitions/cloneFormDefinition'
import seasonShowMixin from '../../../mixins/season.show.mixin'
import GameService from '../../../services/game.service'
import router from '@/router'
import vue2Dropzone from 'vue2-dropzone'
import { TokenService } from '@/services/storage.service'
import { useExternalLinks } from '@/use/useExternalLinks'
import { useTimeline } from '@/use/useTimeline'
import LeGenerateGameImage from '@/components/le/LeGenerateGameImage.vue'
import LeScheduleGame from '@/components/le/LeScheduleGame.vue'
import LeYTLive from '@/components/le/LeYTLive.vue'
import LeRegenerateGameCode from '@/components/le/LeRegenerateGameCode.vue'
import { formattedGameCode } from '@/lib/gameCode'
import ScheduleService from '../../../services/schedule.service'

export default {
  mixins: [seasonShowMixin],
  components: {
    LeRegenerateGameCode,
    LeYTLive,
    LeScheduleGame,
    LeGenerateGameImage,
    vueDropzone: vue2Dropzone
  },
  data () {
    return {
      venues: [],
      moveGameErrorMessage: null,
      moveGameLoading: false,
      teamForfeitForm: {},
      teamForfeitModel: {
        withPunishment: true
      },
      teamForfeitFields: [
        {
          key: 'teamForfeit',
          type: 'ntm-select',
          options: [],
          templateOptions: {
            customLabel: (t) => {
              return t.name
            },
            label: 'teamThatForfeit'
          }
        },
        {
          key: 'withPunishment',
          type: 'boolean',
          templateOptions: {
            label: 'withPunishment'
          }
        }
      ],
      playerSwapForm: {},
      playerSwapModel: {},
      playerSwapFields: [
        {
          key: 'from',
          type: 'ntm-select',
          options: [
          ],
          templateOptions: {
            customLabel: (i) => {
              if (i.number_of_jersey) {
                return `#${i.number_of_jersey} - ${i.name}`
              }

              return i.name
            },
            label: 'from'
          }
        },
        {
          key: 'to',
          type: 'ntm-select',
          options: [
          ],
          templateOptions: {
            customLabel: (i) => {
              if (i.number_of_jersey) {
                return `#${i.number_of_jersey} - ${i.name}`
              }

              return i.name
            },
            label: 'to'
          }
        }
      ],
      playerSwapLoading: false,
      form: {},
      liveGameWebsiteLink: null,
      gameStatsForm: {},
      model: {
        schedule: {},
        homeTeam: {},
        awayTeam: {}
      },
      // Move game to another schedule
      moveGameForm: {},
      moveGameModel: {
        schedule: null,
        round: null
      },
      moveGameFields: [
        {
          key: 'schedule',
          type: 'ntm-select',
          options: [],
          templateOptions: {
            label: 'schedule',
            customLabel: (o) => `${o.name ? o.name : ''}`
          }
        },
        {
          key: 'round',
          type: 'input',
          templateOptions: {
            label: 'round',
            attrs: {
              type: 'number'
            }
          }
        }
      ],
      cloneModel: {
        gameUuid: ''
      },
      cloneForm: {},
      playerForm: {},
      playerStat: {},
      playerStatFields: [],
      currentPlayer: [],
      cloneFields: cloneFields,
      gameStatsFields: gameStatsFields,
      // fields: fields,
      clubSelected: false
    }
  },
  setup () {
    const { fetchCurrentTimeline } = useTimeline()
    const { getGameWebsiteUrl } = useExternalLinks()
    return {
      fetchCurrentTimeline,
      getGameWebsiteUrl
    }
  },
  mounted () {
    this.liveGameWebsiteLink = this.getGameWebsiteUrl(this.model)
  },
  // beforeRouteUpdate (to, from, next) {
  //   GameService.show(to.params.uuid, to.params.game).then((response) => {
  //     this.setData(response.data)
  //     next()
  //   })
  // },
  beforeRouteEnter (to, from, next) {
    GameService.show(to.params.uuid, to.params.game).then((response) => {
      next(vm => vm.setData(response.data))
    })
  },
  methods: {
    openMoveGameModal () {
      // prefill round with current
      this.moveGameModel.round = this.model.round
      const scheduleField = this.moveGameFields.find(f => f.key === 'schedule')
      if (scheduleField) scheduleField.options = []

      const seasonUuid = this.model?.season?.uuid || this.model?.schedule?.season?.uuid || this.$store.state.currentSeason?.uuid
      ScheduleService.index('', seasonUuid).then((response) => {
        const options = response.data || response
        const currentScheduleUuid = this.model?.schedule?.uuid
        const filteredOptions = Array.isArray(options)
          ? options.filter(o => o?.uuid !== currentScheduleUuid)
          : []

        if (this.moveGameModel?.schedule?.uuid === currentScheduleUuid) {
          this.moveGameModel.schedule = null
        }

        if (scheduleField) scheduleField.options = filteredOptions
        this.$nextTick(() => {
          this.$modal.show('move-game')
        })
      }).finally(() => {
        this.$store.commit('loaded')
      })
    },
    resetMoveGameForm () {
      this.moveGameModel = {
        schedule: null,
        round: null
      }
      if (this.moveGameForm) this.moveGameForm.$errors = []
      this.moveGameErrorMessage = null
      this.moveGameLoading = false
    },
    submitMoveGame () {
      if (this.moveGameLoading) return
      this.moveGameErrorMessage = null
      this.moveGameLoading = true
      GameService.moveGameToAnotherSchedule(this.model.schedule.uuid, this.model.uuid, {
        scheduleUuid: this.moveGameModel.schedule.uuid,
        round: this.moveGameModel.round
      }).then(() => {
        this.$nextTick(() => {
          this.$modal.hide('move-game')
        })
        router.push({ name: 'schedules.show', params: { uuid: this.$route.params.uuid } })
      }).catch((err) => {
        // If backend returns plain message key with 422, show it translated inside the same modal
        if (err?.response?.status === 422) {
          const key = err?.response?.data
          this.moveGameErrorMessage = typeof key === 'string' ? key : 'error'
        }
        this.$store.commit('loaded')
      }).finally(() => {
        this.moveGameLoading = false
      })
    },
    openTeamForfeitModal () {
      this.teamForfeitFields[0].options = [
        this.model.homeTeam,
        this.model.awayTeam
      ]
      this.$modal.show('team-forfeit')
    },
    submitTeamForfeit () {
      GameService.teamForfeit(this.model.schedule.uuid, this.model.uuid, {
        teamForfeitUuid: this.teamForfeitModel.teamForfeit?.uuid,
        withPunishment: this.teamForfeitModel.withPunishment
      }).then(() => {
        this.$modal.hide('team-forfeit')
        this.fetchData()
        this.$store.commit('loaded')
      }).catch((err) => {
        this.teamForfeitForm.$errors = err.response.data.errors
      })
    },
    formattedGameCode,
    setData (data) {
      this.model = data
    },
    fetchData () {
      GameService.show(this.model.schedule.uuid, this.model.uuid).then((res) => {
        this.setData(res.data)
      })
    },
    resetStats () {
      GameService.reset(this.model.schedule.uuid, this.model.uuid).then(() => {
        this.fetchData()
        this.$store.commit('loaded')
      })
    },
    editMode () {
      GameService.editMode(this.model.schedule.uuid, this.model.uuid).then(() => {
        this.model.status = 'FINISHED'
        this.$store.dispatch('fetchFinishedGames')
      })
    },
    archive () {
      GameService.archive(this.model.schedule.uuid, this.model.uuid).then(() => {
        this.model.status = 'ARCHIVED'
        this.$store.dispatch('fetchFinishedGames')
      })
    },
    playerStatsModal (teamUuid, playerUuid) {
      const t = this
      t.playerStatFields = []
      t.currentPlayer = {}
      t.playerForm.$errors = []
      GameService.getPlayerStats(t.model.schedule.uuid, t.model.uuid, teamUuid, playerUuid).then((response) => {
        t.currentPlayer = {
          playerUuid: playerUuid,
          teamUuid: teamUuid
        }
        const fields = response.data.fields
        fields.forEach(function (f) {
          const o = {
            key: 'email',
            type: 'input',
            templateOptions: {}
          }
          if (f.type === 'boolean') {
            o.type = 'boolean'
          }
          o.key = f.key
          o.templateOptions.label = f.key
          switch (f.type) {
            case 'integer':
              o.templateOptions.inputType = 'number'
              break
            case 'boolean':
              o.templateOptions.inputType = 'boolean'
              break
            case 'string':
              o.templateOptions.inputType = 'text'
              break
          }
          o.templateOptions.wrapperClasses = 'col-md-6 pull-left'
          t.playerStatFields.push(o)
        })
        t.playerStat = response.data.model
        t.$refs.playerStats.open()
      })
    },
    updatePlayerStats () {
      const t = this
      t.playerStatFields.forEach(function (i) {
        if (i.templateOptions.inputType === 'number') {
          t.playerStat[i.key] = parseInt(t.playerStat[i.key])
        }
      })
      GameService.updatePlayerStats(t.model.schedule.uuid, t.model.uuid, t.currentPlayer.teamUuid, t.currentPlayer.playerUuid, t.playerStat).then(() => {
        t.$refs.playerStats.close()
        this.$store.commit('loaded')
      }).catch((err) => {
        this.playerForm.$errors = err.response.data.errors
      })
    },
    deletePlayerStats (player) {
      GameService.deletePlayerStats(this.model.schedule.uuid, this.model.uuid, player.teamUuid, player.uuid).then(() => {
        this.fetchData()
        this.$store.commit('loaded')
      }).catch((err) => {
        this.playerForm.$errors = err.response.data.errors
      })
    },
    addPeriod () {
      if (!this.model.periods) {
        this.$set(this.model, 'periods', [])
      }
      const periodNumber = this.model.periods.length + 1
      const periodType = periodNumber <= this.config.periods ? 'REGULAR' : 'OVERTIME'
      this.model.periods.push({
        periodNumber: periodNumber,
        periodType: periodType,
        homeScore: 0,
        awayScore: 0
      })
    },
    removePeriod (index) {
      const period = this.model.periods[index]
      if (period && period.uuid) {
        return
      }
      this.model.periods.splice(index, 1)
      // Re-calculate period numbers and types
      this.model.periods.forEach((period, i) => {
        period.periodNumber = i + 1
        period.periodType = period.periodNumber <= this.config.periods ? 'REGULAR' : 'OVERTIME'
      })
    },
    onSubmit () {
      GameService.update(this.$route.params.uuid, this.model.uuid, this.model).then(() => {
        this.fetchData()
        this.fetchCurrentTimeline()
        this.$store.commit('loaded')
      }).catch((err) => {
        this.form.$errors = err.response.data.errors
      })
    },
    onCloneSubmit () {
      GameService.clone(this.$route.params.uuid, this.model.uuid, this.cloneModel.gameUuid).then(() => {
        router.push({ name: 'schedules.show', params: { uuid: this.$route.params.uuid } })
      }).catch((err) => {
        this.form.$errors = err.response.data.errors
        this.$store.commit('loaded')
      })
    },
    fakeStats () {
      GameService.fakeStats(this.model.schedule.uuid, this.model.uuid, {}).then(() => {
        this.fetchData()
        this.$toast.fire({
          title: 'Fake stats generation started',
          icon: 'success'
        })
      })
    },
    onStatSubmit () {
      // Ensure periods are present
      if (!this.model.periods) {
        this.model.periods = []
      }
      GameService.updateStats(this.$route.params.uuid, this.model.uuid, this.model).then(() => {
        this.$store.commit('loaded')
      }).catch((err) => {
        this.gameStatsForm.$errors = err.response.data.errors
      })
    },
    deleteGame () {
      GameService.destroy(this.model.schedule.uuid, this.model.uuid).then(() => {
        this.$router.push({ name: 'schedules.show', params: { uuid: this.model.schedule.uuid } })
      })
    },
    playerSwapSubmit () {
      this.playerSwapLoading = true

      GameService.swapPlayerStats(this.model.schedule.uuid, this.model.uuid, this.playerSwapModel).then(() => {
        this.fetchData()
        this.$modal.hide('swap-player-stats')
      }).catch((err) => {
        this.playerSwapForm.$errors = err.response.data.errors
      }).finally(() => {
        this.playerSwapLoading = false
      })
    },
    playerSwapOpen (team) {
      this.setPlayerSwapOptions(team)
      this.$modal.show('swap-player-stats')
    },
    setPlayerSwapOptions (team) {
      this.playerSwapFields[0].options = this.model[team].players
      this.playerSwapFields[1].options = this.model[team].players
    },
    resetPlayerSwapModel () {
      this.playerSwapModel.from = null
      this.playerSwapModel.to = null
    }
  },
  watch: {
    finishedGames: 'fetchData',
    homeTeamScore (val) {
      this.$set(this.model, 'home_team_score', val)
    },
    awayTeamScore (val) {
      this.$set(this.model, 'away_team_score', val)
    }
  },
  computed: {
    infoFields () {
      const fields = [

        {
          key: 'report',
          type: 'html',
          templateOptions: {
            label: 'report'
          }
        },
        {
          key: 'embedVideoCode',
          type: 'textarea',
          templateOptions: {
            attrs: {
              rows: 5
            },
            label: 'embedVideoCode'
          }
        },
        {
          key: 'is_exhibition',
          type: 'boolean',
          templateOptions: {
            label: 'is_exhibition'
          }
        }
      ]

      if (this.model.type !== 'PLAYOFF') {
        fields.push({
          key: 'type',
          type: 'ntm-select',
          isString: true,
          options: [
            'LEAGUE',
            'FRIENDLY'
          ],
          templateOptions: {
            translated: true,
            label: 'type'
          }
        })

        fields.push({
          key: 'round',
          type: 'input',
          templateOptions: {
            label: 'round'
          }
        })
      }

      return fields
    },
    gameStatusClass () {
      return {
        'badge-success': ['FINISHED', 'ARCHIVED'].includes(this.model.status),
        'badge-danger': this.model.status === 'LIVE',
        'badge-info': this.model.status === 'SCHEDULED',
        'badge-default': this.model.status === 'NOT_PLAYED'
      }
    },
    currentClient () {
      return this.$store.state.currentClient
    },
    homeTeamScore () {
      let score = Number(this.model.home_team_score_bonus || 0)
      if (this.model.periods) {
        this.model.periods.forEach(p => {
          score += Number(p.homeScore || 0)
        })
      }
      return score
    },
    awayTeamScore () {
      let score = Number(this.model.away_team_score_bonus || 0)
      if (this.model.periods) {
        this.model.periods.forEach(p => {
          score += Number(p.awayScore || 0)
        })
      }
      return score
    },
    finishedGames () {
      return this.$store.state.finishedGames
    },
    dropzoneHomeTeam () {
      return {
        url: `${process.env.VUE_APP_BASE_URL}api/v2/admin/import/game/${this.model.uuid}/team/${this.model.homeTeam.uuid}`,
        headers: {
          'X-ORGANISATION': this.currentClient.uuid,
          Authorization: `Bearer ${TokenService.getToken()}`
        },
        acceptedFiles: 'text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
      }
    },
    dropzoneAwayTeam () {
      return {
        url: `${process.env.VUE_APP_BASE_URL}api/v2/admin/import/game/${this.model.uuid}/team/${this.model.awayTeam.uuid}`,
        headers: {
          'X-ORGANISATION': this.currentClient.uuid,
          Authorization: `Bearer ${TokenService.getToken()}`
        },
        acceptedFiles: 'text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
      }
    },
    isEditMode () {
      return ['FINISHED'].includes(this.model.status)
    },
    isArchived () {
      return ['ARCHIVED'].includes(this.model.status)
    },
    user () {
      return this.$store.state.user
    },
    config () {
      return this.$store.state.config
    },
    baseHomeTeamStatsFields () {
      return this.homeTeamStatsFields
    },
    baseAwayTeamStatsFields () {
      return this.awayTeamStatsFields
    },
    homeTeamStatsFields () {
      const fields = [
        {
          key: 'home_team_score',
          type: 'input',
          templateOptions: {
            inputType: 'number',
            label: 'score'
          }
        }
      ]
      if (this.config) {
        if (this.config.has_score_bonus) {
          fields.push({
            key: 'home_team_score_bonus',
            type: 'input',
            templateOptions: {
              inputType: 'number',
              label: 'bonusPoints'
            }
          })
        }
      }

      return fields
    },
    awayTeamStatsFields () {
      const fields = [
        {
          key: 'away_team_score',
          type: 'input',
          templateOptions: {
            inputType: 'number',
            label: 'score'
          }
        }
      ]
      if (this.config) {
        if (this.config.has_score_bonus) {
          fields.push({
            key: 'away_team_score_bonus',
            type: 'input',
            templateOptions: {
              inputType: 'number',
              label: 'bonusPoints'
            }
          })
        }
      }

      return fields
    }
  }
}
</script>
<style>
@media (max-width: 768px) {
  .qr-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-bottom: 20px;
  }
}
</style>
